/**
 * Handles keyboard and mouse navigation for a menu component.
 * Implements accessible navigation patterns including submenu toggling,
 * keyboard navigation (arrows, escape), and click-outside behavior.
 *
 * @param {HTMLElement} menuElement - The root menu container element
 * @returns {void}
 */
const menu = menuElement => {
    if (!menuElement) return;

    const submenuToggles = Array.from(menuElement.querySelectorAll('.menu__submenu-toggle'));

    const closeAllSubmenus = () => {
        submenuToggles.forEach(toggle => {
            toggle.setAttribute('aria-expanded', 'false');
        });
    };

    /**
     * Handles submenu toggle button clicks
     * @param {Event} event - Click event
     */
    const toggleSubmenu = (event) => {
        const button = event.currentTarget;
        button.setAttribute('aria-expanded',
            button.getAttribute('aria-expanded') !== 'true');

        // Close all other submenus
        submenuToggles
            .filter(toggle => toggle !== button)
            .forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
    };

    /**
     * Handles keyboard navigation within the menu
     * @param {KeyboardEvent} event - Keyboard event
     */
    const keyDownHandler = event => {
        const { key, target } = event;
        const currentSubmenu = target.closest('.menu__submenu');

        if (currentSubmenu) {
            handleSubmenuNavigation(event, currentSubmenu);
        } else {
            handleTopLevelNavigation(event);
        }
    };

    /**
     * Handles keyboard navigation within a submenu
     * @param {KeyboardEvent} event - Keyboard event
     * @param {HTMLElement} currentSubmenu - Current submenu element
     */
    const handleSubmenuNavigation = (event, currentSubmenu) => {
        const { key, target, shiftKey } = event;
        const submenuLinks = Array.from(currentSubmenu.querySelectorAll('.menu__link'));
        const currentIndex = submenuLinks.indexOf(target);

        switch (key) {
            case 'ArrowDown':
            case 'ArrowUp':
                event.preventDefault();
                const nextIndex = key === 'ArrowDown'
                    ? (currentIndex + 1) % submenuLinks.length
                    : (currentIndex - 1 + submenuLinks.length) % submenuLinks.length;
                submenuLinks[nextIndex].focus();
                break;

            case 'Tab':
                event.preventDefault();
                const tabIndex = shiftKey
                    ? (currentIndex - 1 + submenuLinks.length) % submenuLinks.length
                    : (currentIndex + 1) % submenuLinks.length;
                submenuLinks[tabIndex].focus();
                break;

            case 'Escape':
                event.preventDefault();
                const parentToggle = currentSubmenu.closest('li').querySelector('.menu__submenu-toggle');
                closeAllSubmenus();
                parentToggle?.focus();
                break;
        }
    };

    /**
     * Handles keyboard navigation for top-level menu items
     * @param {KeyboardEvent} event - Keyboard event
     */
    const handleTopLevelNavigation = (event) => {
        const { key, target } = event;
        const topLevelItems = Array.from(
            menuElement.querySelectorAll('.menu > li > .menu__submenu-toggle, .menu > li > .menu__link')
        );
        const currentIndex = topLevelItems.indexOf(target);

        switch (key) {
            case 'ArrowLeft':
            case 'ArrowRight':
                event.preventDefault();
                const nextIndex = key === 'ArrowRight'
                    ? (currentIndex + 1) % topLevelItems.length
                    : (currentIndex - 1 + topLevelItems.length) % topLevelItems.length;
                topLevelItems[nextIndex].focus();
                break;

            case 'ArrowDown':
                if (target.classList.contains('menu__submenu-toggle')) {
                    event.preventDefault();
                    if (target.getAttribute('aria-expanded') === 'false') {
                        target.setAttribute('aria-expanded', 'true');
                    }
                    const submenu = target.closest('li').querySelector('.menu__submenu');
                    submenu?.querySelector('.menu__link')?.focus();
                }
                break;

            case 'Escape':
                event.preventDefault();
                closeAllSubmenus();
                break;
        }
    };

    // Event Listeners
    submenuToggles.forEach(toggle => toggle.addEventListener('click', toggleSubmenu));

    document.addEventListener('click', (event) => {
      if (!menuElement.contains(event.target)) closeAllSubmenus();
    });

    menuElement.addEventListener('keydown', keyDownHandler);

    // Back button handling
    menuElement.querySelectorAll('.menu__back-button').forEach(button => {
        button.addEventListener('click', () => {
            const submenuToggle = button.closest('.menu__submenu').previousElementSibling;
            submenuToggle?.setAttribute('aria-expanded', 'false');
        });
    });
};

export default menu;

